import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useAuth } from "@west-tennessee-consulting/ui";
import { logger } from "@/lib/logger";
import type { BillingSummaryResponse } from "@/lib/types/reports";
import type { Database } from "@/lib/supabase/database.types";
import { getHubBrowserClient } from "@/lib/supabase/typed-browser-client";
import {
  fetchClientReportTimeEntries,
  fetchReportTimeEntries,
} from "@/lib/reports/fetch-time-entries";
import {
  entryHours,
  type ReportClientTimeEntryRow,
  type ReportTimeEntryRow,
} from "@/lib/reports/time-entry-fields";

export interface BillingReportFilters {
  date_from?: string;
  date_to?: string;
  user_id?: string;
  contact_id?: string;
}

function userName(row: ReportTimeEntryRow): string {
  const name = (row.user_name ?? "").trim();
  return name.length > 0 ? name : "Unknown User";
}

function clientName(
  row: Pick<ReportTimeEntryRow, "company_name" | "contact_name">
): string {
  const name = (row.company_name ?? row.contact_name ?? "").trim();
  return name.length > 0 ? name : "Unknown Contact";
}

/**
 * CLIENT-SAFE billed amount (Option 1 — client-facing totals only).
 * Sums `tickets.total_cost` — the CLIENT-facing cost per LABOR_COST_BURDEN
 * (`Σ billable hours × billable_rate`, maintained by a DB trigger) — over the
 * distinct tickets in the period's billable entries. This gives the client a
 * real billed total WITHOUT `billable_rate` ever crossing the wire. RLS scopes
 * the tickets to the client's own. Note: `total_cost` is whole-ticket, so a
 * ticket spanning periods contributes its full cost; an exact date-windowed
 * Σ(hours×rate) would require a server-side aggregate (future upgrade).
 */
async function sumClientBilledFromTickets(
  supabase: SupabaseClient<Database>,
  ticketIds: string[]
): Promise<number> {
  const ids = Array.from(new Set(ticketIds.filter((id): id is string => !!id)));
  if (ids.length === 0) return 0;

  let total = 0;
  const CHUNK = 500;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const { data, error } = await supabase
      .from("tickets")
      .select("id, total_cost")
      .in("id", ids.slice(i, i + CHUNK));
    if (error) {
      throw new Error(`Failed to fetch ticket billing totals: ${error.message}`);
    }
    for (const row of data ?? []) {
      const cost =
        typeof row.total_cost === "number" ? row.total_cost : Number(row.total_cost);
      if (Number.isFinite(cost)) total += cost;
    }
  }
  return total;
}

/**
 * Billing report data — codegen-typed via denormalized `time_entries` columns
 * (no nested-relation joins, no `any`/`unwrapRelation`). Billable entries only,
 * matching the /reports/billing financial view. Revenue = billable_rate × hours.
 */
export function useBillingReportData(
  filters: BillingReportFilters,
  isClientUser: boolean,
  clientId?: string
) {
  return useQuery({
    queryKey: ["billing-reports", filters, isClientUser, clientId],
    queryFn: async () => {
      const supabase = getHubBrowserClient();
      const companyScope =
        (isClientUser && clientId ? clientId : undefined) ?? filters.contact_id;

      // CLIENT-SAFE path: never fetch billable_rate / internal columns. Rows
      // carry hours only; per-entry revenue is suppressed (the client-facing
      // billed total is surfaced in useBillingMetrics from tickets.total_cost).
      if (isClientUser) {
        let clientRows: ReportClientTimeEntryRow[];
        try {
          clientRows = await fetchClientReportTimeEntries(supabase, {
            start: filters.date_from ?? "1900-01-01",
            end: filters.date_to ?? "2999-12-31",
            userId: filters.user_id || undefined,
            companyId: companyScope || undefined,
          });
        } catch (error) {
          logger.error("Error fetching billing reports", error);
          throw new Error("Failed to fetch billing data");
        }

        const processedData = clientRows
          .filter((entry) => entry.entry_type === "billable")
          .map((entry) => ({
            id: entry.id,
            contact_name: clientName(entry),
            ticket_title: entry.ticket_title || "Unknown Ticket",
            date_worked: entry.date_worked,
            hours_worked: entryHours(entry),
            billable_rate: null,
            revenue: null,
          }));

        return { data: processedData, total: processedData.length };
      }

      let rows: ReportTimeEntryRow[];
      try {
        rows = await fetchReportTimeEntries(supabase, {
          start: filters.date_from ?? "1900-01-01",
          end: filters.date_to ?? "2999-12-31",
          userId: filters.user_id || undefined,
          companyId: companyScope || undefined,
        });
      } catch (error) {
        logger.error("Error fetching billing reports", error);
        throw new Error("Failed to fetch billing data");
      }

      const processedData = rows
        .filter((entry) => entry.entry_type === "billable")
        .map((entry) => {
          const hours = entryHours(entry);
          const rate = entry.billable_rate ?? 0;
          return {
            id: entry.id,
            user_name: userName(entry),
            contact_name: clientName(entry),
            ticket_title: entry.ticket_title || "Unknown Ticket",
            date_worked: entry.date_worked,
            hours_worked: hours,
            billable_rate: entry.billable_rate,
            revenue: rate && hours ? rate * hours : 0,
          };
        });

      return { data: processedData, total: processedData.length };
    },
    enabled: true,
  });
}

/**
 * Billing metric-card figures, FLAT snake_case so they map straight onto
 * `BillingMetrics` (the report framework reads snake_case keys — camelCase or a
 * `{ metrics: {…} }` wrapper silently renders as 0). See `toBillingMetrics`.
 *
 * - CLIENT path (client_user): hours + client-facing billed total only; no rate,
 *   no AR, no cost ever crosses the wire.
 * - EMPLOYEE/ADMIN path: the real numbers come from the server route
 *   `/api/reports/billing/summary` — revenue + AR (QuickBooks) for everyone,
 *   `net_cost` + `profit_margin` ONLY when the caller is `wtc_admin` (gated at
 *   the source so pay rates never reach the browser).
 */
export function useBillingMetrics(
  filters: BillingReportFilters,
  isClientUser: boolean,
  clientId?: string,
  options?: { includeAr?: boolean }
) {
  const { getAuthToken } = useAuth();
  const includeAr = options?.includeAr !== false;
  return useQuery({
    queryKey: ["billing-metrics", filters, isClientUser, clientId, includeAr],
    queryFn: async (): Promise<Partial<BillingSummaryResponse>> => {
      const companyScope =
        (isClientUser && clientId ? clientId : undefined) ?? filters.contact_id;

      // CLIENT-SAFE path: billed total from tickets.total_cost (client-facing
      // cost) so billable_rate/AR/cost never reach the client.
      if (isClientUser) {
        const supabase = getHubBrowserClient();
        let clientRows: ReportClientTimeEntryRow[];
        try {
          clientRows = await fetchClientReportTimeEntries(supabase, {
            start: filters.date_from ?? "1900-01-01",
            end: filters.date_to ?? "2999-12-31",
            userId: filters.user_id || undefined,
            companyId: companyScope || undefined,
          });
        } catch (error) {
          logger.error("Error fetching billing metrics", error);
          throw new Error("Failed to fetch billing metrics");
        }

        const billable = clientRows.filter((entry) => entry.entry_type === "billable");
        const totalEntries = billable.length;
        const totalRevenue = await sumClientBilledFromTickets(
          supabase,
          billable.map((entry) => entry.ticket_id).filter((id): id is string => !!id)
        );
        const clients = new Set(
          billable.map((entry) => entry.company_name ?? entry.contact_name ?? "unknown")
        );
        const totalContacts = clients.size;

        return {
          total_count: totalEntries,
          filtered_count: totalEntries,
          total_revenue: totalRevenue,
          total_contacts: totalContacts,
          avg_revenue_per_contact: totalContacts > 0 ? totalRevenue / totalContacts : 0,
          outstanding_amount: 0,
          paid_amount: 0,
          discount_amount: 0,
          invoice_count: 0,
          realms_scanned: 0,
        };
      }

      // EMPLOYEE/ADMIN path: server computes revenue + margin (admin-gated) + AR.
      const token = await getAuthToken();
      const params = new URLSearchParams();
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      if (companyScope) params.set("company_id", companyScope);
      if (!includeAr) params.set("include_ar", "false");

      const res = await fetch(`/api/reports/billing/summary?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      if (!res.ok) {
        logger.error("Billing summary request failed", undefined, {
          status: res.status,
        });
        throw new Error("Failed to fetch billing metrics");
      }
      return (await res.json()) as BillingSummaryResponse;
    },
    enabled: true,
  });
}
